import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Save, X, UserPlus, Search } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";

const cap = (s) => (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "");

function PrivilegeCard({ priv, roles, users, onSaved }) {
  const [selRoles, setSelRoles] = useState(priv.roles || []);
  const [selUsers, setSelUsers] = useState(priv.user_ids || []);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const dirty =
    JSON.stringify([...selRoles].sort()) !== JSON.stringify([...(priv.roles || [])].sort()) ||
    JSON.stringify([...selUsers].sort()) !== JSON.stringify([...(priv.user_ids || [])].sort());

  const toggleRole = (code) =>
    setSelRoles((r) => (r.includes(code) ? r.filter((x) => x !== code) : [...r, code]));
  const addUser = (id) => setSelUsers((u) => (u.includes(id) ? u : [...u, id]));
  const removeUser = (id) => setSelUsers((u) => u.filter((x) => x !== id));

  const userById = (id) => users.find((u) => u.id === id);

  const filtered = search.trim()
    ? users
        .filter((u) => !selUsers.includes(u.id))
        .filter((u) =>
          `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(search.toLowerCase()),
        )
        .slice(0, 8)
    : [];

  async function save() {
    setSaving(true);
    try {
      await api.put(`/privileges/${priv.key}`, { roles: selRoles, user_ids: selUsers });
      toast.success(`Updated access for "${priv.label}"`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="hah-card p-5" data-testid={`priv-card-${priv.key}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#0B7E8C]" />
          <h3 className="text-base font-semibold text-[#1F2937]">{priv.label}</h3>
        </div>
        <code className="text-[11px] text-[#94A3B8]">{priv.key}</code>
      </div>
      <p className="text-sm text-[#6B7280] mb-4">{priv.description}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Roles */}
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">Grant to roles</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
            {roles.map((r) => (
              <label key={r.code} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <Checkbox
                  checked={selRoles.includes(r.code)}
                  onCheckedChange={() => toggleRole(r.code)}
                  data-testid={`priv-role-${priv.key}-${r.code}`}
                />
                <span className="text-[#1F2937] truncate">{r.name || cap(r.code)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Individual users */}
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">Grant to individual users</div>
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users by name or email…"
              className="pl-8"
              data-testid={`priv-user-search-${priv.key}`}
            />
            {filtered.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg overflow-hidden">
                {filtered.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => { addUser(u.id); setSearch(""); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F5F7FA] flex items-center justify-between"
                    data-testid={`priv-user-opt-${u.id}`}
                  >
                    <span className="text-sm text-[#1F2937]">{u.first_name} {u.last_name} <span className="text-xs text-[#94A3B8]">{u.email}</span></span>
                    <UserPlus className="w-4 h-4 text-[#0B7E8C]" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selUsers.length === 0 && <span className="text-xs text-[#94A3B8] italic">No individual users assigned.</span>}
            {selUsers.map((id) => {
              const u = userById(id);
              return (
                <span key={id} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-[#EAF8FA] text-[#0B7E8C]">
                  {u ? `${u.first_name} ${u.last_name}` : id}
                  <button onClick={() => removeUser(id)} data-testid={`priv-user-remove-${id}`}><X className="w-3 h-3" /></button>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={!dirty || saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`priv-save-${priv.key}`}>
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

export default function AccessControl() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/privileges");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="access-control-page">
      <PageHeader
        title="Access Control"
        subtitle="Grant feature privileges to roles or individual users. Admins always hold every privilege."
      />
      {loading ? (
        <div className="p-6 text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <div className="space-y-4">
          {(data?.items || []).map((priv) => (
            <PrivilegeCard
              key={priv.key}
              priv={priv}
              roles={data.roles || []}
              users={data.users || []}
              onSaved={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}
